/* import __COLOCATED_TEMPLATE__ from './user-content.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import type PartGroup from 'embercom/objects/inbox/conversation-stream/part-group';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import type UserComment from 'embercom/objects/inbox/renderable/user-comment';
import type QuickReplyResponse from 'embercom/objects/inbox/renderable/quick-reply-response';
import type UserSummary from 'embercom/objects/inbox/user-summary';
import type CustomBotSummary from 'embercom/objects/inbox/custom-bot-summary';
import type Conversation from 'embercom/objects/inbox/conversation';
import { inject as service } from '@ember/service';
import type Session from 'embercom/services/session';
import { RenderableType } from 'embercom/models/data/inbox/renderable-types';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import {
  getLanguageFromPartGroup,
  isTranslatingPartGroup,
  translatedPartExists,
  hasTranslationStatusInPartGroup,
  getAllTranslationLanguageCodesFromPartGroup,
  getOriginalLanguageCodeFromPartGroup,
  getLanguageLabelFromCode,
} from '../helpers/translations';
import type ConversationTranslationSettings from 'embercom/services/conversation-translation-settings';
import type IntlService from 'ember-intl/services/intl';
import {
  nextInCycle,
  normalizeLocale,
  getDefaultWorkspaceLocale,
} from '../helpers/language-selection';
import { computeTranslationUi } from '../helpers/translation-ui';
import type User from 'embercom/objects/inbox/user';
interface UserRenderablePart extends RenderablePart {
  renderableData: UserComment | QuickReplyResponse;
}

interface UserPartGroup extends PartGroup {
  parts: Array<UserRenderablePart>;
}

interface Args {
  partGroup: UserPartGroup;
  conversation: Conversation;
  isReadOnly: boolean;
  duplicateData?: { users: User[]; moreExist: boolean };
}

interface Signature {
  Args: Args;
}

export default class ConversationStreamUserContentComponent extends Component<Signature> {
  @service session!: Session;
  @service declare conversationTranslationSettings: ConversationTranslationSettings;
  @service declare intl: IntlService;
  @tracked selectedLangCode?: string;

  get isSpamViewEnabled(): boolean {
    return this.session.workspace.isFeatureEnabled('fin-service-agent-spam-view');
  }

  get availableTranslationCodes(): string[] {
    return getAllTranslationLanguageCodesFromPartGroup(this.args.partGroup);
  }

  get originalLanguageCode(): string | undefined {
    return getOriginalLanguageCodeFromPartGroup(this.args.partGroup);
  }

  get defaultLanguageCode(): string | undefined {
    return getDefaultWorkspaceLocale(this.session);
  }

  private get translationUi() {
    return computeTranslationUi({
      session: this.session,
      available: this.availableTranslationCodes,
      preferred: this.session.teammate?.teammatePreferredLocale,
      defaultLang: this.defaultLanguageCode,
      original: this.originalLanguageCode,
      selectedLangCode: this.selectedLangCode,
    });
  }

  get cycleOrderCodes(): string[] {
    return this.translationUi.cycleOrderCodes;
  }

  get currentSelectedCode(): string | undefined {
    return this.translationUi.currentSelectedCode;
  }

  get selectedTranslationLanguageCode(): string | undefined {
    return this.currentSelectedCode;
  }

  get selectedLanguageLabel(): string {
    let code = this.selectedTranslationLanguageCode;
    if (!code) {
      return '';
    }
    return getLanguageLabelFromCode(code, this.intl.primaryLocale) || '';
  }

  get renderableData() {
    return this.args.partGroup.parts[0].renderableData;
  }

  get user(): UserSummary {
    return this.renderableData.userSummary;
  }

  get customBot(): CustomBotSummary | undefined {
    if (this.renderableData.renderableType === RenderableType.UserComment) {
      return this.renderableData.customBotSummary;
    }
    return undefined;
  }

  get isQuickReplyResponse() {
    return this.renderableData.renderableType === RenderableType.QuickReplyResponse;
  }

  get autoTranslationEnabled() {
    return this.conversationTranslationSettings.autoTranslationEnabledForConversationObject(
      this.args.conversation,
    );
  }

  get showOriginalMessage(): boolean {
    if (!this.translatedPartExists) {
      return true;
    }
    if (this.args.isReadOnly) {
      return (
        normalizeLocale(this.currentSelectedCode) === normalizeLocale(this.originalLanguageCode)
      );
    }
    if (!this.autoTranslationEnabled) {
      return true;
    }
    return normalizeLocale(this.currentSelectedCode) === normalizeLocale(this.originalLanguageCode);
  }

  get showTranslatedMessage() {
    return !this.showOriginalMessage;
  }

  get translatedPartExists() {
    return translatedPartExists(this.args.partGroup);
  }

  get isTranslating() {
    return isTranslatingPartGroup(this.args.partGroup);
  }

  get originalLanguage() {
    return getLanguageFromPartGroup(this.args.partGroup, this.intl.primaryLocale, true) || '';
  }

  get showTranslationToggle() {
    // Always show if there's a translation status (even if translation failed or was filtered)
    if (hasTranslationStatusInPartGroup(this.args.partGroup)) {
      return true;
    }

    // In read-only mode, show translation toggle if translated content exists
    if (this.args.isReadOnly) {
      return this.translatedPartExists;
    }

    // In normal mode, require auto translation to be enabled
    return this.autoTranslationEnabled && (this.translatedPartExists || this.isTranslating);
  }

  // Duplicate detection logic (uses data passed from conversation page)
  get potentialDuplicates() {
    return this.args.duplicateData || { users: [], moreExist: false };
  }

  get hasImpersonationWarning(): boolean {
    return this.potentialDuplicates.users.length > 0;
  }

  get leadSharesEmailWithUser(): boolean {
    // Check if current user is a lead and has duplicate users
    return this.user?.role === 'contact_role' && this.hasImpersonationWarning;
  }

  @action toggleTranslation() {
    let order = this.cycleOrderCodes;
    let next = nextInCycle(this.currentSelectedCode, order);
    if (next) {
      this.selectedLangCode = next;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::PartGroups::UserContent': typeof ConversationStreamUserContentComponent;
    'inbox2/conversation-stream/part-groups/user-content': typeof ConversationStreamUserContentComponent;
  }
}
